import type { GameState, Player } from '../types/game'
import type { AuctionState } from '../types/auction'
import type { AIDecision } from '../ai/types'
import { MediumAIStrategy } from '../ai/strategies/medium/strategy'

/**
 * Wrapper for Medium AI Strategy with simplified interface
 * This provides a cleaner interface to use the existing medium AI without complex dependencies
 */
export class MediumAIWrapper {
  private strategies = new Map<string, MediumAIStrategy>()

  /**
   * Get or create a medium AI strategy for a player
   */
  private getStrategy(player: Player): MediumAIStrategy {
    if (!this.strategies.has(player.id)) {
      const strategy = new MediumAIStrategy({
        difficulty: 'medium',
        seed: Date.now() + Math.random() * 1000
      })
      this.strategies.set(player.id, strategy)
    }
    return this.strategies.get(player.id)!
  }

  /**
   * Make a decision for the player in the current auction
   */
  async makeAuctionDecision(
    player: Player,
    auction: AuctionState,
    gameState: GameState
  ): Promise<AIDecision | null> {
    // Simulate thinking time (faster for open auctions due to 7s timer)
    const isOpenAuction = auction.type === 'open' ||
      (auction.type === 'double' && auction.embeddedAuction?.type === 'open')
    const delay = isOpenAuction
      ? 50 + Math.random() * 100  // 50-150ms for open auctions
      : 100 + Math.random() * 200  // Normal delay for other auctions
    await new Promise(resolve => setTimeout(resolve, delay))

    const strategy = this.getStrategy(player)

    // Create a minimal context with just what we need
    const playerIndex = gameState.players.findIndex(p => p.id === player.id)

    // DEBUG: Verify gameState and playerIndex
    console.log('[WRAPPER DEBUG] Creating context:', {
      playerName: player.name,
      playerId: player.id,
      playerIndex,
      totalPlayers: gameState.players.length,
      playerIds: gameState.players.map(p => p.id),
      found: playerIndex !== -1
    })

    const minimalContext = {
      gameState: gameState,  // CRITICAL: Provide the actual game state
      roundNumber: gameState.round.roundNumber,
      currentPhase: gameState.round.phase.type,
      playerIndex: playerIndex,  // Must match what strategy expects
      players: gameState.players.map(p => ({
        id: p.id,
        name: p.name,
        handCount: p.hand.length,
        money: p.money
      })),
      myHand: player.hand,
      myMoney: player.money
    }
    let decisionType: string

    switch (auction.type) {
      case 'one_offer':
        if (auction.phase === 'bidding') {
          decisionType = 'one_offer_bid'
          const decision = await strategy.makeDecision(decisionType as any, minimalContext, {
            auction,
            currentBid: auction.currentBid  // CRITICAL: Pass currentBid from auction state
          })
          return this.adaptOneOfferDecision(decision, player, auction)
        } else if (auction.phase === 'auctioneer_decision' && player.id === auction.auctioneerId) {
          return this.makeAuctioneerDecision(player, auction)
        }
        break

      case 'open':
        decisionType = 'bid'
        const openDecision = await strategy.makeDecision(decisionType as any, minimalContext, {
          auction,
          currentBid: auction.currentBid  // CRITICAL: Pass currentBid for raise calculation
        })
        return this.adaptOpenAuctionDecision(openDecision, player, auction)

      case 'hidden':
        decisionType = 'hidden_bid'
        const hiddenDecision = await strategy.makeDecision(decisionType as any, minimalContext, { auction })
        return this.adaptHiddenAuctionDecision(hiddenDecision, player, auction)

      case 'fixed_price':
        if (auction.phase === 'price_setting') {
          // Price setting phase - auctioneer sets the price
          if (player.id === auction.auctioneerId) {
            decisionType = 'fixed_price'
            const fixedDecision = await strategy.makeDecision(decisionType as any, minimalContext, { auction })
            return this.adaptFixedPriceDecision(fixedDecision, player, auction)
          }
        } else if (auction.phase === 'buying') {
          // Buying phase - players decide to buy or pass
          if (auction.turnOrder[auction.currentTurnIndex] === player.id) {
            decisionType = 'buy'
            const buyDecision = await strategy.makeDecision(decisionType as any, minimalContext, { auction })
            return this.adaptFixedPriceDecision(buyDecision, player, auction)
          }
        }
        break

      case 'double':
        // Check if it's this player's turn using turnOrder, NOT currentAuctioneerId
        // currentAuctioneerId only changes when someone offers a card
        if (!auction.secondCard) {
          const currentPlayerId = auction.turnOrder?.[auction.currentTurnIndex]
          if (currentPlayerId === player.id) {
            return this.makeDoubleOfferDecision(player, auction)
          }
        }
        // Handle embedded auction if second card is offered
        if (auction.secondCard && auction.embeddedAuction) {
          // Delegate to embedded auction type
          return this.makeAuctionDecision(player, auction.embeddedAuction, gameState)
        }
        break

      default:
        return null
    }

    return null
  }

  /**
   * Adapt One Offer bidding decision from medium AI format
   */
  private adaptOneOfferDecision(decision: any, player: Player, auction: any): AIDecision | null {
    if (decision.action === 'bid' && decision.amount) {
      return {
        type: 'bid',
        action: 'bid',
        amount: decision.amount
      }
    } else if (decision.action === 'pass') {
      return {
        type: 'bid',
        action: 'pass'
      }
    }
    return null
  }

  /**
   * Make auctioneer decision in One Offer auction
   */
  private async makeAuctioneerDecision(player: Player, auction: any): Promise<AIDecision | null> {
    // Simple logic for now - can be enhanced
    if (auction.currentBid === 0 || !auction.currentBidderId) {
      // No bids - take for free
      return {
        type: 'bid',
        action: 'take_free'
      }
    }

    const bidValue = auction.currentBid

    // Simple auctioneer logic: accept if bid is decent, otherwise outbid
    // Accept if bid is > 20k or bid is > 60% of our money
    if (bidValue > 20 || bidValue > player.money * 0.6) {
      return {
        type: 'bid',
        action: 'accept'
      }
    }

    // Outbid by minimum if we can afford it
    const outbidAmount = bidValue + 1
    if (outbidAmount <= player.money) {
      return {
        type: 'bid',
        action: 'outbid',
        amount: outbidAmount
      }
    }

    // Default to accept
    return {
      type: 'bid',
      action: 'accept'
    }
  }

  /**
   * Adapt open auction decision
   */
  private adaptOpenAuctionDecision(decision: any, player: Player, auction: any): AIDecision | null {
    if (decision.action === 'bid' && decision.amount) {
      return {
        type: 'bid',
        action: 'bid',
        amount: decision.amount
      }
    } else if (decision.action === 'pass') {
      return {
        type: 'bid',
        action: 'pass'
      }
    }
    return null
  }

  /**
   * Adapt hidden auction decision
   */
  private adaptHiddenAuctionDecision(decision: any, player: Player, auction: any): AIDecision | null {
    // Hidden bid decisions have 'amount' directly (no 'action' field)
    // The type is 'hidden_bid' from the strategy
    if (decision.amount !== undefined && decision.amount >= 0) {
      return {
        type: 'bid',
        action: 'bid',
        amount: decision.amount
      }
    } else if (decision.action === 'bid' && decision.amount !== undefined) {
      // Also handle cases where action is explicitly set
      return {
        type: 'bid',
        action: 'bid',
        amount: decision.amount
      }
    } else if (decision.action === 'pass') {
      // Pass = bid 0 in hidden auctions
      return {
        type: 'bid',
        action: 'bid',
        amount: 0
      }
    }
    return null
  }

  /**
   * Adapt fixed price decision
   */
  private adaptFixedPriceDecision(decision: any, player: Player, auction: any): AIDecision | null {
    // Handle price setting (from strategy)
    if (decision.action === 'set_price' || decision.price !== undefined) {
      return {
        type: 'bid',
        action: 'set_price',
        amount: decision.price || decision.amount
      }
    }
    // Handle buy/pass decisions (from strategy)
    else if (decision.action === 'buy') {
      return {
        type: 'bid',
        action: 'buy'
      }
    } else if (decision.action === 'pass') {
      return {
        type: 'bid',
        action: 'pass'
      }
    }
    return null
  }

  /**
   * Make double auction offer decision
   */
  private makeDoubleOfferDecision(player: Player, auction: any): AIDecision | null {
    const matchingCards = player.hand.filter(
      card => card.artist === auction.doubleCard.artist && card.auctionType !== 'double'
    )

    if (matchingCards.length > 0) {
      return {
        type: 'bid',
        action: 'offer',
        cardId: matchingCards[0].id
      }
    }

    // No matching card - decline to offer, so the next player can try
    // This is critical: return a decision rather than null so the auction progresses
    return {
      type: 'bid',
      action: 'decline'
    }
  }

}